import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, BookOpen, Globe2, Award, CheckCircle2, ArrowRight,
  Compass, Target, Layers, Languages, Mic2, HelpCircle, ShieldAlert,
  GraduationCap, Briefcase, Quote, HeartHandshake, FileCheck
} from "lucide-react";
import { Reveal, SectionLabel, GradientText } from "@/components/ui-bits";
import founderImg from "@/assets/founder.jpg";

export default function About() {
  return (
    <div className="relative overflow-hidden py-16 sm:py-24">
      {/* Top Background Glows */}
      <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[800px] rounded-full bg-electric/15 blur-[120px]" />
      <div className="pointer-events-none absolute right-0 top-1/3 h-[400px] w-[500px] rounded-full bg-violet/15 blur-[100px]" />

      <div className="cv-container">
        {/* Header Breadcrumb & Tag */}
        <Reveal className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full border border-electric/30 bg-electric/10 px-4 py-1.5 text-xs font-semibold text-electric backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" /> Founder Story · Aparajita Sudarshan
          </span>
          <h1 className="mt-6 font-heading text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl leading-[1.08]">
            CVOLVE PRO Was Built Because
            <br />
            <GradientText className="inline-block">I Needed It Myself</GradientText>
          </h1>
          <p className="mt-6 text-lg sm:text-xl leading-relaxed text-muted-foreground">
            From a small town in Uttar Pradesh to leading Learning & Development across 6 countries — and discovering firsthand that the modern job search process is broken for even the most qualified candidates.
          </p>
        </Reveal>

        {/* Founder Bio Card */}
        <Reveal delay={0.1} className="mt-16">
          <div className="relative overflow-hidden rounded-[2.5rem] border border-border/80 bg-card/70 p-6 backdrop-blur-xl shadow-2xl sm:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
              {/* Image Column */}
              <div className="lg:col-span-5 flex flex-col items-center text-center">
                <div className="relative group">
                  <div className="absolute -inset-1.5 rounded-full bg-gradient-to-tr from-electric via-violet to-aqua opacity-75 blur-md group-hover:opacity-100 transition duration-500" />
                  <div className="relative h-56 w-56 sm:h-64 sm:w-64 overflow-hidden rounded-full border-4 border-background shadow-xl">
                    <img
                      src={founderImg}
                      alt="Aparajita Sudarshan, Founder of CVOLVE PRO"
                      className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.src = "/founder.jpg";
                      }}
                    />
                  </div>
                  <div className="absolute bottom-2 right-4 rounded-full bg-navy/90 border border-electric/40 px-3.5 py-1 text-xs font-bold text-aqua shadow-lg backdrop-blur">
                    Founder & Creator
                  </div>
                </div>

                <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight">Aparajita Sudarshan</h3>
                <p className="text-sm font-medium text-electric">Founder, CVOLVE PRO</p>
                <p className="text-xs text-muted-foreground mt-1">Author · L&D Specialist · Expatriate Job Seeker</p>

                {/* Country Journey Badges */}
                <div className="mt-5 flex flex-wrap items-center justify-center gap-1.5 max-w-sm">
                  {[
                    { flag: "🇮🇳", name: "India" },
                    { flag: "🇧🇭", name: "Bahrain" },
                    { flag: "🇶🇦", name: "Qatar" },
                    { flag: "🇲🇾", name: "Malaysia" },
                    { flag: "🇦🇪", name: "Dubai" },
                    { flag: "🇬🇧", name: "United Kingdom" },
                  ].map((c, i) => (
                    <span
                      key={c.name}
                      className="inline-flex items-center gap-1 rounded-lg border border-border/60 bg-secondary/60 px-2.5 py-1 text-xs font-medium"
                    >
                      <span>{c.flag}</span>
                      <span className="text-muted-foreground">{c.name}</span>
                      {i < 5 && <span className="text-muted-foreground/40 text-[10px] ml-0.5">→</span>}
                    </span>
                  ))}
                </div>
              </div>

              {/* Founder Summary & Key Creds */}
              <div className="lg:col-span-7 space-y-6">
                <div className="space-y-4 text-base sm:text-lg leading-relaxed text-muted-foreground">
                  <p>
                    My name is <strong className="text-foreground font-semibold">Aparajita Sudarshan</strong>, Founder of <strong className="text-foreground font-semibold">CVOLVE PRO</strong>.
                  </p>
                  <p>
                    I come from a small town in Uttar Pradesh, India. My career has since taken me across <strong className="text-foreground">India, Bahrain, Qatar, Malaysia, Dubai, and now the United Kingdom</strong>.
                  </p>
                  <p>
                    I have spent nearly two decades working in Learning and Development, teaching, building learning programmes, working across cultures and industries, and helping people develop their careers.
                  </p>
                </div>

                {/* Books Showcase */}
                <div className="rounded-2xl border border-electric/20 bg-electric/5 p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-electric flex items-center gap-2">
                    <BookOpen className="h-4 w-4" /> Published Author
                  </p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-xl border border-border/50 bg-background/80 p-3.5 shadow-sm">
                      <p className="font-heading text-sm font-bold text-foreground">CPC Coding Quick Guide</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Essential Exam Review</p>
                    </div>
                    <div className="rounded-xl border border-border/50 bg-background/80 p-3.5 shadow-sm">
                      <p className="font-heading text-sm font-bold text-foreground">From First Job to Leadership</p>
                      <p className="text-xs text-muted-foreground mt-0.5">The Ultimate ATS and AI Resume Guide</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-border/60 bg-secondary/40 p-4 text-sm font-medium text-foreground flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet/10 text-violet">
                    <GraduationCap className="h-5 w-5" />
                  </div>
                  <span>
                    "So when I moved to the UK, I thought I understood careers. Then I became a job seeker. And I realised how much I did not know."
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* The Journey & Story Sections */}
        <div className="mt-20 max-w-4xl mx-auto space-y-16">
          {/* Chapter 1 */}
          <Reveal>
            <div className="relative pl-6 sm:pl-10 border-l-2 border-electric/40 space-y-4">
              <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full border-2 border-electric bg-background" />
              <SectionLabel color="text-electric">Chapter 1</SectionLabel>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                I had the experience. I had the qualifications. I had even written the books.
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-base sm:text-lg">
                <p>
                  But none of that automatically translated into interviews.
                </p>
                <p>
                  I arrived in a country where I knew almost nobody professionally. I was an expatriate entering a new hiring market with different expectations, different recruitment practices and an entirely different way of presenting experience.
                </p>
                <p>
                  At first, I thought a strong career history would speak for itself. <strong>It did not.</strong>
                </p>
                <div className="grid gap-3 pt-2 sm:grid-cols-2">
                  <div className="rounded-xl border border-border bg-card p-4">
                    <CheckCircle2 className="h-5 w-5 text-coral mb-2" />
                    <p className="text-sm font-semibold text-foreground">Writing a CV is not a complete history dump</p>
                    <p className="text-xs text-muted-foreground mt-1">It's not simply listing everything you've ever done.</p>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-4">
                    <CheckCircle2 className="h-5 w-5 text-electric mb-2" />
                    <p className="text-sm font-semibold text-foreground">ATS score is not the final destination</p>
                    <p className="text-xs text-muted-foreground mt-1">Getting through recruitment is about genuine role alignment.</p>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-4">
                    <CheckCircle2 className="h-5 w-5 text-violet mb-2" />
                    <p className="text-sm font-semibold text-foreground">One CV cannot fit 50 jobs</p>
                    <p className="text-xs text-muted-foreground mt-1">Sending generic applications burns opportunities.</p>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-4">
                    <CheckCircle2 className="h-5 w-5 text-pink mb-2" />
                    <p className="text-sm font-semibold text-foreground">STAR interviewing is critical</p>
                    <p className="text-xs text-muted-foreground mt-1">Recruitment demands structured, quantified impact stories.</p>
                  </div>
                </div>
                <p className="font-semibold text-foreground italic pt-2">
                  "I was learning how to become a job seeker while actually being one. That experience changed everything."
                </p>
              </div>
            </div>
          </Reveal>

          {/* Chapter 2 */}
          <Reveal>
            <div className="relative pl-6 sm:pl-10 border-l-2 border-violet/40 space-y-4">
              <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full border-2 border-violet bg-background" />
              <SectionLabel color="text-violet">Chapter 2</SectionLabel>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                The problem was bigger than writing a CV
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-base sm:text-lg">
                <p>
                  Being without a job is difficult. But searching for one can almost become a full-time job of its own.
                </p>
                <p>
                  One website has one set of vacancies. Another has completely different jobs. Then there are company career pages, specialist job boards, remote job sites and recruitment platforms.
                </p>
                <div className="rounded-2xl border border-border/80 bg-secondary/30 p-6 space-y-2 text-sm font-medium">
                  <p className="text-muted-foreground">🔄 You search → You shortlist → You rewrite your CV → You write another cover letter → You apply.</p>
                  <p className="text-muted-foreground">📑 Then you try to remember which version of your CV went where.</p>
                  <p className="text-muted-foreground">🎙️ Then an interview arrives and suddenly you are preparing STAR examples, researching the company and trying to predict what you might be asked.</p>
                  <p className="text-foreground font-bold">Then you start all over again.</p>
                </div>
                <div className="rounded-2xl bg-gradient-to-r from-electric/10 to-violet/10 border border-electric/30 p-5 text-center">
                  <p className="font-heading text-xl font-bold text-foreground">
                    "Why is all of this scattered across so many different tools?"
                  </p>
                  <p className="text-sm font-medium text-electric mt-1">That question became CVOLVE PRO.</p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Chapter 3 */}
          <Reveal>
            <div className="relative pl-6 sm:pl-10 border-l-2 border-aqua/40 space-y-4">
              <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full border-2 border-aqua bg-background" />
              <SectionLabel color="text-aqua">Chapter 3</SectionLabel>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                One job search. One place.
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-base sm:text-lg">
                <p>
                  I started building the kind of platform I wished I had when I began my own search. CVOLVE PRO is designed to bring the major parts of the job search together.
                </p>

                <div className="grid gap-4 sm:grid-cols-2 pt-2">
                  <div className="card-surface p-5 space-y-2">
                    <div className="flex items-center gap-2 text-electric font-heading font-bold text-base">
                      <Target className="h-5 w-5" /> Diagnostic ATS Insights
                    </div>
                    <p className="text-sm text-muted-foreground">
                      An ATS score is a diagnostic, not the destination. We show you what is working and what is missing without superficial tricks.
                    </p>
                  </div>
                  <div className="card-surface p-5 space-y-2">
                    <div className="flex items-center gap-2 text-violet font-heading font-bold text-base">
                      <HelpCircle className="h-5 w-5" /> Authentic Experience Probing
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Before rewriting, CVOLVE asks targeted questions about your real background to position your genuine experience against the JD.
                    </p>
                  </div>
                  <div className="card-surface p-5 space-y-2">
                    <div className="flex items-center gap-2 text-pink font-heading font-bold text-base">
                      <Mic2 className="h-5 w-5" /> STAR & Live Interview Prep
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Prepares you for STAR-based interview questions and natural face-to-face conversational practice with AI feedback.
                    </p>
                  </div>
                  <div className="card-surface p-5 space-y-2">
                    <div className="flex items-center gap-2 text-aqua font-heading font-bold text-base">
                      <Globe2 className="h-5 w-5" /> Global Job Aggregator
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Aggregates verified opportunities into one place instead of requiring candidates to hop between 10 job sites every single morning.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Chapter 4 */}
          <Reveal>
            <div className="relative pl-6 sm:pl-10 border-l-2 border-lime/40 space-y-4">
              <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full border-2 border-lime bg-background" />
              <SectionLabel color="text-lime">Chapter 4</SectionLabel>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                Careers are global. Career tools should be too.
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-base sm:text-lg">
                <p>
                  My own career has crossed six countries. That made something else obvious to me:
                </p>
                <p>
                  The world of work is international, but many career technologies still behave as though every candidate applies in the same language, in the same market, using the same recruitment conventions. <strong>They do not.</strong>
                </p>
                <p>
                  Billions of people around the world do not use English as their first or preferred language. That is why CVOLVE PRO is being built with multilingual career support, including <strong className="text-foreground">English, German, Spanish, and French</strong>, allowing candidates to create job-specific CVs and cover letters for opportunities beyond a single language or geography.
                </p>
                <p className="font-semibold text-foreground">
                  Because talent is global. Opportunity should be easier to reach.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Chapter 5 */}
          <Reveal>
            <div className="relative pl-6 sm:pl-10 border-l-2 border-coral/40 space-y-4">
              <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full border-2 border-coral bg-background" />
              <SectionLabel color="text-coral">Chapter 5</SectionLabel>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                And yes, job searching is partly a numbers game
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-base sm:text-lg">
                <p>
                  One thing I learned through my own search is that there is rarely one magical application. You need persistence, volume and consistency.
                </p>
                <p>
                  But sending hundreds of generic applications is not a strategy either. The real challenge is being able to apply at scale <strong className="text-foreground">without sacrificing relevance and quality</strong>.
                </p>

                <div className="rounded-2xl border border-border bg-card p-6">
                  <p className="font-heading font-bold text-foreground mb-3 text-base">The CVOLVE PRO Workflow:</p>
                  <ul className="space-y-2.5 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2.5"><CheckCircle2 className="h-4 w-4 text-lime shrink-0" /> Find the right opportunities with targeted aggregations.</li>
                    <li className="flex items-center gap-2.5"><CheckCircle2 className="h-4 w-4 text-lime shrink-0" /> Understand what the employer is asking for in depth.</li>
                    <li className="flex items-center gap-2.5"><CheckCircle2 className="h-4 w-4 text-lime shrink-0" /> Identify what is missing from your current application.</li>
                    <li className="flex items-center gap-2.5"><CheckCircle2 className="h-4 w-4 text-lime shrink-0" /> Bring out the experience you genuinely have.</li>
                    <li className="flex items-center gap-2.5"><CheckCircle2 className="h-4 w-4 text-lime shrink-0" /> Create a targeted CV and tailored cover letter.</li>
                    <li className="flex items-center gap-2.5"><CheckCircle2 className="h-4 w-4 text-lime shrink-0" /> Prepare properly for the STAR interview.</li>
                    <li className="flex items-center gap-2.5"><CheckCircle2 className="h-4 w-4 text-lime shrink-0" /> Move to the next opportunity without having to rebuild everything from zero.</li>
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Chapter 6 */}
          <Reveal>
            <div className="relative pl-6 sm:pl-10 border-l-2 border-electric/40 space-y-4">
              <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full border-2 border-electric bg-background" />
              <SectionLabel color="text-electric">Chapter 6</SectionLabel>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                What CVOLVE PRO will never promise
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-base sm:text-lg">
                <p>
                  I cannot promise anyone a job. No technology can honestly do that. The final decision belongs to an employer.
                </p>
                <p>
                  But I can make the road towards that decision <strong className="text-foreground">less confusing, less repetitive and far more intelligent</strong>.
                </p>
                <p>
                  CVOLVE PRO is still evolving because job searching is evolving. Recruitment is changing. AI is changing. Interviews are changing. The way employers discover candidates is changing.
                </p>
                <p>
                  And I am still a job seeker myself. That matters.
                </p>
                <p>
                  Because I am not building CVOLVE PRO while looking at the problem from the outside. <strong className="text-foreground">I am building it while living the problem.</strong>
                </p>
                <p>
                  Every frustrating application, every interview, every rejection, every improvement and every lesson gives me another question to ask:
                </p>
                <p className="font-heading text-lg font-bold text-electric">
                  "How can I make this easier for the next person?"
                </p>
                <p>
                  That is how CVOLVE PRO started. And that is how it will continue to evolve.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Founder Quote Highlight Box */}
        <Reveal delay={0.2} className="mt-20 max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-navy via-[#131d3d] to-navy p-8 sm:p-12 text-center text-cream shadow-2xl border border-electric/30">
            <Quote className="mx-auto h-12 w-12 text-electric/40 mb-4" />
            <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black leading-tight tracking-tight max-w-2xl mx-auto">
              "Career searching should be hard because the competition is good, not because the process is broken."
            </h3>
            <div className="mt-6 flex flex-col items-center justify-center">
              <p className="font-heading font-bold text-lg text-aqua">Aparajita Sudarshan</p>
              <p className="text-xs text-muted-foreground">Founder, CVOLVE PRO</p>
            </div>
          </div>
        </Reveal>

        {/* Bottom CTA */}
        <Reveal delay={0.25} className="mt-16 text-center">
          <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Ready to experience a more intelligent job search?
          </h3>
          <p className="mt-2 text-muted-foreground max-w-xl mx-auto text-sm sm:text-base">
            Build your targeted CV, run diagnostic ATS checks, and prepare for interviews in one single platform.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            <Link to="/signup" className="cv-btn-primary">
              Start Free <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/#jobs" className="cv-btn-ghost border border-border">
              Explore Open Jobs
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
